/**
 * Shared formatting utilities for the application
 */

/**
 * Format date string to display format (DD MMM YYYY)
 * @param dateString - ISO date string or date string
 * @returns Formatted date string (e.g., "03 Mar 2026")
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateString || '';
  }
};

/**
 * Format date-time string to "MMM DD, YYYY, hh:mm AM/PM"
 * Example: "Feb 20, 2026, 02:45 PM"
 */
export const formatDateTimeWithTime = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return dateString || "";
  }
};

/**
 * Format price to Indian Rupee format
 * @param price - Price value as number
 * @returns Formatted price string (e.g., "₹1,499")
 */
export const formatPrice = (price: number): string => {
  return `₹${price.toLocaleString('en-IN')}`;
};

/**
 * Format date for date input fields (YYYY-MM-DD)
 * @param dateString - ISO date string
 * @returns Formatted date string for input fields
 */
export const formatDateForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

export const formatDateDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
};

export const formatApiDateDisplay = (dateValue?: string): string => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return formatDateDisplay(`${year}-${month}-${day}`);
};

export const formatApiTimeDisplay = (dateValue?: string): string => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};
